import { intersection } from "lodash";
import { useCallback, useMemo } from "react";
import { getJwt } from "utils/jwt";

export enum Role {
  USER = "ROLE_USER",
  HQ = "ROLE_HQ",
  DS = "ROLE_DS",
  DM = "ROLE_DM",
  AM = "ROLE_AM"
}

interface CurrentUser {
  employeeId: number;
  username: string;
  firstName: string;
  lastName: string;
  fullName: string;
  isSectorManager: boolean;
  isShopManager: boolean;
  roles: Role[];
  roleDisplay: "HQ" | "DS" | "DM" | "AM" ;
  nickname: string;
  iat: number;
  exp: number;
}

// https://stackoverflow.com/a/38552302
const parseJwt = (token: string) => {
  var base64Url = token.split(".")[1];
  var base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
  var jsonPayload = decodeURIComponent(
    window
      .atob(base64)
      .split("")
      .map(function (c) {
        return "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2);
      })
      .join("")
  );

  return JSON.parse(jsonPayload);
};

const getCurrentUser = () => {
  const jwt = getJwt();
  if (!jwt) return null;

  return parseJwt(jwt) as CurrentUser;
};

export enum Feature {
  ShopAdd,
  ShopFilters,
  ShopFiltersSectorManagerFilter,
  ShopEdit,
  ShopPropose,
  ShopNotify,
  CustomerJourneys,
  Stats,
  DeleteProposal,
  ApplyProposal,
  ShopComparator,
  Pdf
}

const AUTHORIZATIONS = {
  [Feature.ShopAdd]: [Role.HQ],
  [Feature.ShopEdit]: [Role.HQ, Role.DS],
  [Feature.ShopFilters]: [Role.HQ, Role.DS, Role.AM],
  [Feature.ShopFiltersSectorManagerFilter]: [Role.HQ],
  [Feature.ShopPropose]: [Role.HQ, Role.DS, Role.DM,Role.AM],
  [Feature.ShopNotify]: [Role.HQ, Role.DS,Role.AM],
  [Feature.CustomerJourneys]: [Role.USER],
  [Feature.Stats]: [Role.HQ,Role.AM],
  [Feature.DeleteProposal]: [Role.HQ,Role.AM],
  [Feature.ApplyProposal]: [Role.HQ,Role.AM],
  [Feature.ShopComparator]: [Role.USER],
  [Feature.Pdf]: [Role.USER],
};

const useAuthorization = () => {
  const user = getCurrentUser();
  const hasAccess = useCallback(
    (feature: Feature) => {
      if (!user) return false;
      return intersection(AUTHORIZATIONS[feature], user.roles).length > 0;
    },
    [user]
  );

  return hasAccess;
};

const useCurrentUser = () => {
  const user = useMemo(() => {
    const user = getCurrentUser();
    if (!user) throw new Error("Missing current user");
    console.info("Current user", user);

    return user;
  }, []);

  return user;
};

export { useCurrentUser, useAuthorization, getCurrentUser };
